import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import type PhoneInputField from 'components/PhoneInputField';

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value' | 'onChange'
> & {
  name: string;
  // E.164 ("+13057250566"), or a legacy stored number the user hasn't touched.
  value: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

// The inputs sit inside the wrapper's 1px border, while a plain text input owns
// its border. Pushing the global focus outline out by that 1px lands it where
// other inputs draw theirs, with the grey border still showing inside it.
export const OUTLINE_OUTSIDE_BORDER = { outlineOffset: 1 };

// The divider takes the field's own border colour (forms use different greys),
// so it reads as the same line as the border around it.
export const BORDER_COLOR_OF_FIELD = { borderColor: 'inherit' };

type Field = typeof PhoneInputField;

let fieldPromise: Promise<Field> | undefined;
const loadField = () =>
  (fieldPromise ??= import('components/PhoneInputField').then(
    (module) => module.default,
    (error) => {
      // A failed chunk load must not stick: the next interaction retries.
      fieldPromise = undefined;
      throw error;
    }
  ));

/**
 * Same markup as the real field, which brings libphonenumber (~37 KB gz), so it
 * keeps it out of SEO pages' first JS until used or prefilled.
 */
export const PhoneInput: React.FC<PhoneInputProps> = (props) => {
  const {
    name,
    value,
    onChange,
    className,
    placeholder = 'Phone #',
    disabled,
    ...inputProps
  } = props;
  const [LoadedField, setLoadedField] = useState<Field | null>(null);
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const hadFocus = useRef(false);

  const load = () => {
    loadField()
      .then((field) => {
        hadFocus.current = document.activeElement === inputRef.current;
        setLoadedField(() => field);
      })
      // The plain input stays usable and keeps what was typed.
      .catch(console.error);
  };

  // Autofill can fill the number without ever focusing it.
  useEffect(() => {
    if (value || typed) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, typed]);

  if (LoadedField) {
    return (
      <LoadedField
        {...props}
        pendingInput={typed}
        focusOnMount={hadFocus.current}
      />
    );
  }

  return (
    <div
      className={classnames(
        'group relative flex items-stretch bg-white',
        className,
        '!p-0'
      )}
      onFocus={load}
      onPointerEnter={load}
      onTouchStart={load}
    >
      <input
        aria-label="Country code"
        className="shrink-0 w-[96px] py-2 pl-4 pr-3 bg-transparent"
        style={OUTLINE_OUTSIDE_BORDER}
        value="US +1"
        tabIndex={-1}
        readOnly
        disabled={disabled}
      />
      <span
        aria-hidden="true"
        className="self-center shrink-0 h-5 border-l group-focus-within:invisible"
        style={BORDER_COLOR_OF_FIELD}
      />
      <input
        ref={inputRef}
        inputMode="tel"
        autoComplete="tel"
        {...inputProps}
        name={name}
        className="flex-1 min-w-0 py-2 pl-3 pr-4 bg-transparent"
        style={{ ...inputProps.style, ...OUTLINE_OUTSIDE_BORDER }}
        type="tel"
        placeholder={placeholder}
        disabled={disabled}
        value={typed || value}
        onChange={(event) => setTyped(event.target.value)}
      />
    </div>
  );
};

export default PhoneInput;
